/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import BaseObject from "@shared/BaseObject.js";

export const MANUAL_CONNECTION_TARGET_CLASS = "eh-manual-target"
export const MANUAL_CONNECTION_TARGET_ACTIVE_CLASS = "eh-manual-target-active"

const TARGET_HINT_COLOR = "#2563EB"
const TARGET_HINT_CONSTRAINED_COLOR = "#16A34A"
const TARGET_HINT_PADDING = 18
const TARGET_HINT_LINE_WIDTH = 5
const TARGET_HINT_ACTIVE_LINE_WIDTH = 7
const TARGET_HINT_DASH = [7, 5]
const TARGET_HINT_ALPHA = 0.7
const TARGET_HINT_ACTIVE_ALPHA = 0.38
const FAMILY_EDGE_TARGET_HINT_RADIUS = 14
const FAMILY_EDGE_TARGET_HINT_ACTIVE_RADIUS = 16
const TARGET_HINT_LABEL_PERSON = "Click to Connect to Person"
const TARGET_HINT_LABEL_FAMILY = "Click to Add to Family"
const TARGET_HINT_LABEL_AUX = "Add a Twin"
const TARGET_HINT_LABEL_GAP = 10
const TARGET_HINT_LABEL_PADDING_X = 8
const TARGET_HINT_LABEL_PADDING_Y = 5
const TARGET_HINT_LABEL_RADIUS = 6
const TARGET_HINT_LABEL_FONT = "13px Arial"
const TARGET_HINT_LABEL_BACKGROUND = "#111827"
const TARGET_HINT_LABEL_COLOR = "#FFFFFF"
const TARGET_HINT_LABEL_ALPHA = 0.92
const FAMILY_EDGE_TARGET_HINT_CENTER_GAP = 25

export default class GenogramManualConnectionTargetHintRenderer extends BaseObject {

	constructor({cy, targetShape = "ellipse", targetLabel = TARGET_HINT_LABEL_PERSON, targetColor = TARGET_HINT_COLOR}) {
		super()
		this.cy = cy
		this.targetShape = targetShape
		this.targetLabel = targetLabel
		this.targetColor = targetColor
		this.layer = null
		this.animationFrameId = null
		this._listeners = {
			nodeGeometryChanged: this.requestUpdate.bind(this),
			nodeResized: this.requestUpdate.bind(this),
			nodeClassChanged: this.requestUpdate.bind(this),
			edgeClassChanged: this.requestUpdate.bind(this),
			viewportChanged: this.requestUpdate.bind(this),
		}
		this.ensureLayer()
		this.addListeners()
	}

	addListeners() {
		this.cy.on("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.on("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.on("class", "node", this._listeners.nodeClassChanged)
		this.cy.on("class", "edge", this._listeners.edgeClassChanged)
		this.cy.on("pan zoom resize", this._listeners.viewportChanged)
	}

	ensureLayer() {
		if(this.layer || !this.cy.layers){
			return
		}
		this.layer = this.cy.layers().nodeLayer.insertAfter("canvas")
		this.layer.callback((ctx) => {
			this.renderIntoContext(ctx)
		})
	}

	update() {
		this.layer?.update()
	}

	requestUpdate() {
		if(this.animationFrameId){
			return
		}
		if(typeof requestAnimationFrame !== "function"){
			this.update()
			return
		}
		this.animationFrameId = requestAnimationFrame(() => {
			this.animationFrameId = null
			this.update()
		})
	}

	destroy() {
		this.cy.off("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.off("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.off("class", "node", this._listeners.nodeClassChanged)
		this.cy.off("class", "edge", this._listeners.edgeClassChanged)
		this.cy.off("pan zoom resize", this._listeners.viewportChanged)
		if(this.animationFrameId && typeof cancelAnimationFrame === "function"){
			cancelAnimationFrame(this.animationFrameId)
			this.animationFrameId = null
		}
		this.layer?.remove()
		this.layer = null
	}

	renderIntoContext(ctx) {
		let nodes = this.getTargetHintNodes()
		let edges = this.getTargetHintEdges()
		for(let edge of edges){
			this.drawFamilyEdgeTargetHint(ctx, edge)
		}
		for(let node of nodes){
			this.drawTargetHint(ctx, node)
		}
	}

	getTargetHintNodes() {
		return this.cy.nodes().filter(node => this.isRenderableTargetHintNode(node))
	}

	getTargetHintEdges() {
		return this.cy.edges().filter(edge => this.isRenderableTargetHintEdge(edge))
	}

	isRenderableTargetHintNode(node) {
		if(!node?.isNode()){
			return false
		}
		if(node.data("tempElement")){
			return false
		}
		return node.hasClass(MANUAL_CONNECTION_TARGET_CLASS) || node.hasClass(MANUAL_CONNECTION_TARGET_ACTIVE_CLASS)
	}

	isRenderableTargetHintEdge(edge) {
		if(!edge?.isEdge()){
			return false
		}
		return edge.hasClass(MANUAL_CONNECTION_TARGET_CLASS) || edge.hasClass(MANUAL_CONNECTION_TARGET_ACTIVE_CLASS)
	}

	drawTargetHint(ctx, node) {
		if(node.data("isAuxNode")){
			this.drawAuxNodeTargetHint(ctx, node)
			return
		}
		let isActive = node.hasClass(MANUAL_CONNECTION_TARGET_ACTIVE_CLASS)
		let box = node.boundingBox({
			includeLabels: false,
			includeOverlays: false,
		})
		let radiusX = box.w / 2 + TARGET_HINT_PADDING
		let radiusY = box.h / 2 + TARGET_HINT_PADDING
		let center = {
			x: box.x1 + box.w / 2,
			y: box.y1 + box.h / 2,
		}

		ctx.save()
		ctx.strokeStyle = this.targetColor
		ctx.globalAlpha = isActive ? TARGET_HINT_ACTIVE_ALPHA : TARGET_HINT_ALPHA
		ctx.lineWidth = isActive ? TARGET_HINT_ACTIVE_LINE_WIDTH : TARGET_HINT_LINE_WIDTH
		ctx.setLineDash(isActive ? [] : TARGET_HINT_DASH)
		ctx.beginPath()
		if(this.targetShape === "roundedRect"){
			let cornerRadius = Math.min(12, radiusX, radiusY)
			ctx.roundRect(center.x - radiusX, center.y - radiusY, radiusX * 2, radiusY * 2, cornerRadius)
		}else{
			ctx.ellipse(center.x, center.y, radiusX, radiusY, 0, 0, Math.PI * 2)
		}
		ctx.stroke()
		ctx.restore()
		if(isActive){
			this.drawTargetHintLabel(ctx, this.targetLabel, center.x, center.y - radiusY - TARGET_HINT_LABEL_GAP)
		}
	}

	drawAuxNodeTargetHint(ctx, node) {
		let isActive = node.hasClass(MANUAL_CONNECTION_TARGET_ACTIVE_CLASS)
		let position = node.position()
		let radius = isActive ? FAMILY_EDGE_TARGET_HINT_ACTIVE_RADIUS : FAMILY_EDGE_TARGET_HINT_RADIUS

		ctx.save()
		ctx.strokeStyle = TARGET_HINT_COLOR
		ctx.globalAlpha = isActive ? TARGET_HINT_ACTIVE_ALPHA : TARGET_HINT_ALPHA
		ctx.lineWidth = isActive ? TARGET_HINT_ACTIVE_LINE_WIDTH : TARGET_HINT_LINE_WIDTH
		ctx.setLineDash(isActive ? [] : TARGET_HINT_DASH)
		ctx.beginPath()
		ctx.arc(position.x, position.y, radius, 0, Math.PI * 2)
		ctx.stroke()
		ctx.restore()
		if(isActive){
			this.drawTargetHintLabel(ctx, TARGET_HINT_LABEL_AUX, position.x, position.y - radius - TARGET_HINT_LABEL_GAP)
		}
	}

	drawFamilyEdgeTargetHint(ctx, edge) {
		let isActive = edge.hasClass(MANUAL_CONNECTION_TARGET_ACTIVE_CLASS)
		let placement = this.getFamilyEdgeTargetHintPlacement(edge, isActive)
		let previewAuxNode = this.getActiveFamilyEdgePreviewAuxNode(edge, isActive)
		if(previewAuxNode){
			let position = previewAuxNode.position()
			placement = {
				x: position.x,
				y: position.y,
				isShifted: true,
				isCompromised: false,
			}
		}
		if(!placement){
			return
		}
		let radius = isActive ? FAMILY_EDGE_TARGET_HINT_ACTIVE_RADIUS : FAMILY_EDGE_TARGET_HINT_RADIUS
		let strokeColor = placement.isCompromised ? TARGET_HINT_CONSTRAINED_COLOR : TARGET_HINT_COLOR

		ctx.save()
		ctx.strokeStyle = strokeColor
		ctx.globalAlpha = isActive ? TARGET_HINT_ACTIVE_ALPHA : TARGET_HINT_ALPHA
		ctx.lineWidth = isActive ? TARGET_HINT_ACTIVE_LINE_WIDTH : TARGET_HINT_LINE_WIDTH
		ctx.setLineDash(isActive ? [] : TARGET_HINT_DASH)
		ctx.beginPath()
		ctx.arc(placement.x, placement.y, radius, 0, Math.PI * 2)
		ctx.stroke()
		ctx.restore()
		if(isActive){
			this.drawTargetHintLabel(ctx, TARGET_HINT_LABEL_FAMILY, placement.x, placement.y - radius - TARGET_HINT_LABEL_GAP)
		}
	}

	getActiveFamilyEdgePreviewAuxNode(edge, isActive = false) {
		let result = null
		if(isActive){
			result = this.cy.nodes().filter(node => {
				if(!node.data("isAuxNode") || node.data("isPreview") !== true){
					return false
				}
				return node.data("parentEdgeId") === edge.id()
			})[0] || null
		}
		return result
	}

	getFamilyEdgeTargetHintCenter(edge) {
		let segmentPoints = edge.segmentPoints?.()
		let center = null
		if(segmentPoints?.length >= 2){
			let [pt1, pt2] = segmentPoints
			center = {
				x: (pt1.x + pt2.x) / 2,
				y: pt1.y,
			}
		}
		return center
	}

	getFamilyEdgeTargetHintPlacement(edge, isActive = false) {
		let center = this.getFamilyEdgeTargetHintCenter(edge)
		if(!center){
			return null
		}
		let lineBounds = this.getFamilyEdgeTargetHintBounds(edge)
		if(!lineBounds){
			return {
				x: center.x,
				y: center.y,
				isShifted: false,
			}
		}
		let auxNodeXs = this.getFamilyEdgeAuxNodeXs(edge)
		let minGap = this.getFamilyEdgeTargetHintCenterGap(isActive)
		let placementX = this.getBestFamilyEdgeTargetHintX(center.x, lineBounds, auxNodeXs, minGap)
		return {
			x: placementX.x,
			y: center.y,
			isShifted: Math.abs(placementX.x - center.x) > 0.5,
			isCompromised: placementX.isCompromised,
		}
	}

	getFamilyEdgeTargetHintBounds(edge) {
		let segmentPoints = edge.segmentPoints?.()
		if(segmentPoints?.length < 2){
			return null
		}
		let [pt1, pt2] = segmentPoints
		return {
			x1: Math.min(pt1.x, pt2.x),
			x2: Math.max(pt1.x, pt2.x),
		}
	}

	getFamilyEdgeAuxNodeXs(edge) {
		let auxNodeIds = edge.data("auxNodes") || []
		return auxNodeIds
			.map(nodeId => this.cy.getElementById(nodeId))
			.filter(node => node?.nonempty?.())
			.filter(node => node.data("isPreview") !== true)
			.map(node => node.position().x)
			.sort((a, b) => a - b)
	}

	getFamilyEdgeTargetHintCenterGap(isActive = false) {
		let familyRadius = isActive ? FAMILY_EDGE_TARGET_HINT_ACTIVE_RADIUS : FAMILY_EDGE_TARGET_HINT_RADIUS
		return familyRadius * 2 + FAMILY_EDGE_TARGET_HINT_CENTER_GAP
	}

	getBestFamilyEdgeTargetHintX(centerX, lineBounds, auxNodeXs = [], minGap = 0) {
		let candidates = this.getFamilyEdgeTargetHintCandidates(centerX, lineBounds, auxNodeXs, minGap)
		let allowedCandidate = candidates.find(candidate => this.isAllowedFamilyEdgeTargetHintX(candidate, auxNodeXs, minGap))
		if(allowedCandidate != null){
			return {
				x: allowedCandidate,
				isCompromised: false,
			}
		}
		return {
			x: this.getLeastOverlappingFamilyEdgeTargetHintX(candidates, auxNodeXs),
			isCompromised: true,
		}
	}

	getFamilyEdgeTargetHintCandidates(centerX, lineBounds, auxNodeXs = [], minGap = 0) {
		let candidates = [centerX]
		auxNodeXs.forEach(auxX => {
			candidates.push(this.clampFamilyEdgeTargetHintX(auxX - minGap, lineBounds))
			candidates.push(this.clampFamilyEdgeTargetHintX(auxX + minGap, lineBounds))
		})
		return [...new Set(candidates.map(value => Number(value.toFixed(2))))].sort((a, b) => {
			let distanceDiff = Math.abs(a - centerX) - Math.abs(b - centerX)
			if(distanceDiff !== 0){
				return distanceDiff
			}
			return a - b
		})
	}

	clampFamilyEdgeTargetHintX(value, lineBounds) {
		return Math.min(lineBounds.x2, Math.max(lineBounds.x1, value))
	}

	isAllowedFamilyEdgeTargetHintX(candidateX, auxNodeXs = [], minGap = 0) {
		return auxNodeXs.every(auxX => Math.abs(candidateX - auxX) >= minGap)
	}

	getLeastOverlappingFamilyEdgeTargetHintX(candidates, auxNodeXs = []) {
		let bestCandidate = candidates[0] || 0
		let bestDistance = -1
		candidates.forEach(candidate => {
			let nearestAuxDistance = this.getNearestFamilyEdgeAuxDistance(candidate, auxNodeXs)
			if(nearestAuxDistance > bestDistance){
				bestDistance = nearestAuxDistance
				bestCandidate = candidate
			}
		})
		return bestCandidate
	}

	getNearestFamilyEdgeAuxDistance(candidateX, auxNodeXs = []) {
		if(auxNodeXs.length === 0){
			return Number.POSITIVE_INFINITY
		}
		return Math.min(...auxNodeXs.map(auxX => Math.abs(candidateX - auxX)))
	}

	drawTargetHintLabel(ctx, text, centerX, bottomY) {
		ctx.save()
		ctx.font = TARGET_HINT_LABEL_FONT
		ctx.textAlign = "center"
		ctx.textBaseline = "middle"
		let metrics = ctx.measureText(text)
		let width = metrics.width + TARGET_HINT_LABEL_PADDING_X * 2
		let height = this.getTargetHintLabelHeight()
		let x = centerX - width / 2
		let y = bottomY - height

		ctx.globalAlpha = TARGET_HINT_LABEL_ALPHA
		ctx.fillStyle = TARGET_HINT_LABEL_BACKGROUND
		this.drawRoundedRect(ctx, x, y, width, height, TARGET_HINT_LABEL_RADIUS)
		ctx.fill()

		ctx.globalAlpha = 1
		ctx.fillStyle = TARGET_HINT_LABEL_COLOR
		ctx.fillText(text, centerX, y + height / 2)
		ctx.restore()
	}

	getTargetHintLabelHeight() {
		return 13 + TARGET_HINT_LABEL_PADDING_Y * 2
	}

	drawRoundedRect(ctx, x, y, width, height, radius) {
		let right = x + width
		let bottom = y + height
		ctx.beginPath()
		ctx.moveTo(x + radius, y)
		ctx.lineTo(right - radius, y)
		ctx.quadraticCurveTo(right, y, right, y + radius)
		ctx.lineTo(right, bottom - radius)
		ctx.quadraticCurveTo(right, bottom, right - radius, bottom)
		ctx.lineTo(x + radius, bottom)
		ctx.quadraticCurveTo(x, bottom, x, bottom - radius)
		ctx.lineTo(x, y + radius)
		ctx.quadraticCurveTo(x, y, x + radius, y)
	}
}
